// Forced password rotation. Everyone gets the same policy form since the
// username rollout (2026-10-19); only the minimum length differs — 14 for
// Manager+, 9 for everyone else (helpers/passwordPolicy mirrors the BE's
// PasswordPolicy.js). The BE picks the validator by the session's position.
// The subtitle says why the user is here: an expired password (older than 90
// days, 2026-10-20) names the rule; a forced change keeps the generic line.

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { useTranslation } from "../i18n";
import { usePageTitle } from "../hooks/usePageTitle";
import { apiFetch } from "../api/apiFetch";
import { isManagerPlus, readUserPosition } from "../access/positions";
import LanguageToggle from "../components/LanguageToggle";
import ForgotPasswordButton from "../components/ForgotPasswordButton";
import PasswordFields from "../components/PasswordFields";
import { useSavedLoginsAllowed } from "../components/login/savedLogins";
import { useIsPhone } from "../hooks/useIsPhone";
import { isPolicyCompliant, passwordPolicyFor, PASSWORD_MAX_AGE_DAYS, type PasswordContext } from "../components/helpers/passwordPolicy";
import { clearRequirePasswordChange, readRequirePasswordChangeReason } from "../components/helpers/authFlags";
import { logoutProcessor } from "../components/helpers/logoutProcessor";
import "../styles/change-password.css";

const ChangePassword = () => {
    usePageTitle("change_password_title");
    const navigate = useNavigate();
    const { t } = useTranslation();

    const position = useMemo(() => readUserPosition(), []);
    const policy = useMemo(() => passwordPolicyFor(position), [position]);
    const managerPlus = isManagerPlus(position);
    // Phones get the short hints — every line of copy costs a scroll on a handheld.
    const short = useIsPhone();
    const hint = (key: string) => t(short ? `${key}_short` : key);
    // Settings › Login screen: off = the browser is never offered the new password to save.
    const savedLogins = useSavedLoginsAllowed();
    // Why we are here (login response → helpers/authFlags). "expired" words the
    // subtitle around the 90-day rule; "forced" and an unknown reason keep the
    // generic line.
    const reason = useMemo(() => readRequirePasswordChangeReason(), []);
    const subtitleKey = reason === "expired" ? "change_password_subtitle_expired" : "change_password_subtitle";
    // Who the password is for: its username / name may not appear in it.
    const context = useMemo((): PasswordContext => {
        try {
            const u = JSON.parse(window.localStorage.getItem("user") ?? "null");
            return { username: typeof u?.username === "string" ? u.username : null, name: typeof u?.name === "string" ? u.name : null };
        } catch { return {}; }
    }, []);

    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);

    // If the user lands here without being signed in, send them to login.
    // We don't enforce admin-level auth here — the server gate is the
    // session check inside changeOwnPassword.
    useEffect(() => {
        const isLoggedIn = window.localStorage.getItem("isLoggedIn") === "true";
        if (!isLoggedIn) navigate("/login");
    }, [navigate]);

    const canSubmit = isPolicyCompliant(newPassword, policy, context)
        && newPassword === confirmPassword
        && !submitting;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!canSubmit) return;
        setSubmitting(true);
        try {
            // Pull identity from localStorage so the auto-logging
            // middleware can attribute the audit row. The BE doesn't
            // require these fields — session.user is the canonical
            // source — but the actor* aliases let the audit row land
            // correctly when the auto-logger reads the body.
            const actor: { uid?: number; name?: string; warehouse?: string } = {};
            try {
                const raw = window.localStorage.getItem("user");
                if (raw) {
                    const u = JSON.parse(raw);
                    if (typeof u?.uid === "number") actor.uid = u.uid;
                    if (typeof u?.name === "string") actor.name = u.name;
                    if (typeof u?.warehouse === "string") actor.warehouse = u.warehouse;
                }
            } catch { /* ignore */ }
            const res = await apiFetch(`/login/user/change-password`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ newPassword, ...actor }),
            });
            if (res.status === 401) {
                // apiFetch never treats a /login/* 401 as a dead session —
                // handle it here: the session is gone, start over.
                await logoutProcessor();
                navigate("/login", { replace: true });
                return;
            }
            if (!res.ok) {
                let msg = t("change_password_failed");
                try {
                    const data = await res.json();
                    if (data?.message) msg = data.message;
                } catch { /* keep default */ }
                toast.error(msg, { toastId: "change-password-err" });
                return;
            }
            clearRequirePasswordChange();
            toast.success(t("change_password_done"), { toastId: "change-password-ok" });
            // Operatives land back on the operational home; managers on
            // their dashboard.
            navigate(managerPlus ? "/admin" : "/", { replace: true });
        } catch {
            toast.error(t("change_password_failed"), { toastId: "change-password-err" });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="change-password-page">
            <div className="change-password-card animate-slideUp">
                <LanguageToggle className="change-password-language-toggle" />
                <h1 className="change-password-title">{t("change_password_title")}</h1>
                <p className="change-password-subtitle">{t(short ? `${subtitleKey}_short` : subtitleKey, { days: String(PASSWORD_MAX_AGE_DAYS) })}</p>

                <form className="change-password-form" onSubmit={handleSubmit}>
                    {policy.style === "memorable" && (
                        <p className="change-password-hint">{hint("account_setup_password_hint")}</p>
                    )}
                    <PasswordFields
                        variant="dark"
                        policy={policy}
                        context={context}
                        newPassword={newPassword}
                        confirmPassword={confirmPassword}
                        onNewPasswordChange={setNewPassword}
                        onConfirmPasswordChange={setConfirmPassword}
                        showGenerator
                        autoCompleteMode="new-password"
                        allowSave={savedLogins}
                        disabled={submitting}
                        autoFocus
                    />

                    <div className="change-password-actions">
                        <span />
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={!canSubmit}
                        >
                            {submitting ? t("saving") : t("change_password_submit")}
                        </button>
                    </div>
                    <ForgotPasswordButton className="change-password-forgot" />
                </form>
            </div>
        </div>
    );
};

export default ChangePassword;
